import { Stack, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MapEditorResearchTypeTreeFragment } from "./fragments.generated";

export interface MapEditorResearchTypeValue {
    researchTypeId: string;
    speciesId: string;
}

interface MapEditorResearchTypeTreeProps {
    onChange: (values: MapEditorResearchTypeValue[]) => void;
    researchTypes?: MapEditorResearchTypeTreeFragment[];
    value: MapEditorResearchTypeValue[];
}

export function MapEditorResearchTypeTree({ onChange, researchTypes, value }: MapEditorResearchTypeTreeProps) {
    const { t } = useTranslation("mapEditor");

    const elements = useMemo(() => mapToTreeNodes(researchTypes ?? []), [researchTypes]);

    const selectedItems = useMemo(() => {
        return value.map(element => {
            return `${element.researchTypeId}::${element.speciesId}`;
        });
    }, [value]);

    const handleSelectedItemsChange = useCallback(
        (_: unknown, selection: string[]) => {
            const selectionMappedToOutput = selection
                .map(element => {
                    const [researchTypeId, speciesId] = element.split("::");
                    return { researchTypeId, speciesId };
                })
                .filter(element => {
                    return element.speciesId !== undefined;
                });

            onChange(selectionMappedToOutput);
        },
        [onChange],
    );

    return (
        <Stack direction="column" rowGap={1}>
            <Typography sx={{ paddingInline: 3 / 2 }} variant="overline">
                {t("speciesGroupTree.label")}
            </Typography>

            <RichTreeView
                checkboxSelection
                multiSelect
                items={elements}
                onSelectedItemsChange={handleSelectedItemsChange}
                selectedItems={selectedItems}
                selectionPropagation={{
                    descendants: true,
                    parents: true,
                }}
            />
        </Stack>
    );
}

function mapToTreeNodes(researchTypes: MapEditorResearchTypeTreeFragment[]) {
    return researchTypes.map(researchType => ({
        id: researchType.value,
        label: `${researchType.label}`,
        children: researchType.species.map(species => ({
            id: `${researchType.value}::${species.value}`,
            label: `${species.label}`,
        })),
    }));
}
